import React, { useEffect, useState, useRef } from 'react'
import { Link } from 'react-router-dom'
import { Phone, Mail, MapPin, CheckCircle2, Award, ShieldCheck, ThumbsUp, ArrowRight, Users, Paintbrush, Home as HomeIcon, Settings, Sparkles, ChevronLeft, ChevronRight, Building2, Building, Layers, GraduationCap, FileText, Landmark } from 'lucide-react'
import BeforeAfter from '../components/BeforeAfter'
import FAQSection from '../components/FAQSection'

export default function Home() {
  const [sliderPosition, setSliderPosition] = useState(50)
  const [isDragging, setIsDragging] = useState(false)
  const containerRef = useRef(null)
  const [containerWidth, setContainerWidth] = useState(0)

  const handleMove = (clientX) => {
    if (!containerRef.current) return
    const rect = containerRef.current.getBoundingClientRect()
    const x = clientX - rect.left
    const position = Math.max(0, Math.min(100, (x / rect.width) * 100))
    setSliderPosition(position)
  }

  const handleTouchMove = (e) => {
    if (!isDragging) return
    if (e.touches.length > 0) {
      handleMove(e.touches[0].clientX)
    }
  }

  const handleMouseMove = (e) => {
    if (!isDragging) return
    handleMove(e.clientX)
  }

  useEffect(() => {
    if (!containerRef.current) return
    const updateWidth = () => {
      setContainerWidth(containerRef.current.getBoundingClientRect().width)
    }
    updateWidth()
    window.addEventListener('resize', updateWidth)
    return () => window.removeEventListener('resize', updateWidth)
  }, [])

  useEffect(() => {
    const handleMouseUp = () => setIsDragging(false)
    window.addEventListener('mouseup', handleMouseUp)
    window.addEventListener('touchend', handleMouseUp)
    return () => {
      window.removeEventListener('mouseup', handleMouseUp)
      window.removeEventListener('touchend', handleMouseUp)
    }
  }, [isDragging])

  // SEO Focus
  useEffect(() => {
    document.title = 'Professionelt malerfirma i Østjylland | Malerfirma Villy Poulsen'
    const metaDescriptionTag = document.querySelector('meta[name="description"]')
    if (metaDescriptionTag) {
      metaDescriptionTag.setAttribute(
        'content',
        'Malerfirma Villy Poulsen ApS har mere end 30 års erfaring i branchen. Vi leverer flot og holdbart malerarbejde til både private og erhverv i hele Østjylland.'
      )
    }
  }, [])

  const viHjaelperMed = [
    { title: 'Indvendigt malerarbejde', desc: 'Professionel maling af vægge, lofter, døre, vinduer og fuldspartling med sans for detaljen.', icon: Paintbrush, link: '/indvendig-maling' },
    { title: 'Udvendigt malerarbejde', desc: 'Holdbar maling af træværk, vinduer, udhæng og facader, der beskytter mod vind og vejr.', icon: HomeIcon, link: '/udvendig-maling' },
    { title: 'Renovering og vedligeholdelse', desc: 'Gennemgribende istandsættelse og løbende vedligeholdelse af boliger og erhvervsejendomme.', icon: Settings, link: '/renovering-og-vedligeholdelse' },
    { title: 'Facademaling', desc: 'Flot og modstandsdygtig facademaling, der giver din bygning nyt liv og langvarig beskyttelse.', icon: Sparkles, link: '/facademaling' },
    { title: 'Sprøjtemaling', desc: 'Professionel sprøjtemaling af døre, skabslåger, lofter, radiatorer og møbler med perfekt finish.', icon: Award, link: '/sproejtemaling' }
  ]

  const kundegrupper = [
    { name: 'Private kunder', icon: HomeIcon },
    { name: 'Erhvervskunder', icon: Building2 },
    { name: 'Boligforeninger', icon: Layers },
    { name: 'Private boligselskaber', icon: Building },
    { name: 'Institutioner', icon: GraduationCap },
    { name: 'Licitationsopgaver', icon: FileText },
    { name: 'Offentlige opgaver', icon: Landmark }
  ]

  return (
    <div className="bg-white">
      {/* Hero-sektion */}
      <section className="relative bg-[#20252C] text-white py-24 lg:py-32 overflow-hidden">
        {/* Background Video with Overlay */}
        <div className="absolute inset-0 z-0">
          <video
            src="https://assets.auxodev.dk/assets/bbcf0f14-2bcd-4cd4-98b4-2d62ce00d60f/fdf783a9-6d7e-4370-b1f6-7daee1d6deb8.mp4"
            poster="https://assets.auxodev.dk/assets/bbcf0f14-2bcd-4cd4-98b4-2d62ce00d60f/b8e39e69-f00a-4bfb-8620-29ca62cdd838.jpeg"
            autoPlay
            loop
            muted
            playsInline
            className="w-full h-full object-cover opacity-45"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-950/80 via-gray-900/50 to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-tight">
              Professionelt malerfirma i Østjylland
            </h1>
            <p className="mt-4 text-xl text-[#01529E] font-bold">
              Erfaren malermester til kvalitetsbevidst malerarbejde
            </p>
            <p className="mt-6 text-lg text-gray-300 leading-relaxed max-w-2xl">
              Hos Malerfirma Villy Poulsen ApS får du en erfaren og kvalitetsbevidst maler, der leverer flot og holdbart malerarbejde til både private og erhverv.
            </p>
            <p className="mt-4 text-lg text-gray-300 leading-relaxed max-w-2xl">
              Vi udfører alle former for malerarbejde. Uanset opgavens størrelse lægger vi stor vægt på kvalitet, grundighed og god service fra start til slut.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Link
                to="/kontakt"
                className="px-8 py-4 rounded-xl bg-[#01529E] text-white font-bold text-center hover:bg-[#01529E]/90 transition-all shadow-lg hover:shadow-[#01529E]/20 flex items-center justify-center gap-2 cursor-pointer"
              >
                Kontakt os
              </Link>
              <Link
                to="/kontakt"
                className="px-8 py-4 rounded-xl bg-white/10 text-white font-bold text-center hover:bg-white/20 transition-all border border-white/10 flex items-center justify-center gap-2 cursor-pointer"
              >
                Få et uforpligtende tilbud
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Badges Bar */}
      <section className="bg-gray-50 border-b border-gray-100 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center md:text-left">
            <div className="flex flex-col md:flex-row items-center gap-4 p-4 bg-white rounded-xl shadow-sm border border-gray-100">
              <div className="p-3 rounded-xl bg-[#01529E]/5 text-[#01529E] shrink-0">
                <ShieldCheck className="h-6 w-6" />
              </div>
              <div>
                <h4 className="font-bold text-gray-900 text-sm uppercase tracking-wider">Danske Malermestre</h4>
                <p className="text-gray-500 text-xs mt-0.5">Din garanti for professionelt udført arbejde</p>
              </div>
            </div>
            <div className="flex flex-col md:flex-row items-center gap-4 p-4 bg-white rounded-xl shadow-sm border border-gray-100">
              <div className="p-3 rounded-xl bg-[#01529E]/5 text-[#01529E] shrink-0">
                <Award className="h-6 w-6" />
              </div>
              <div>
                <h4 className="font-bold text-gray-900 text-sm uppercase tracking-wider">Højeste Kreditværdighed</h4>
                <p className="text-gray-500 text-xs mt-0.5">AAA-rating sikrer en stabil og solid samarbejdspartner</p>
              </div>
            </div>
            <div className="flex flex-col md:flex-row items-center gap-4 p-4 bg-white rounded-xl shadow-sm border border-gray-100">
              <div className="p-3 rounded-xl bg-[#01529E]/5 text-[#01529E] shrink-0">
                <ThumbsUp className="h-6 w-6" />
              </div>
              <div>
                <h4 className="font-bold text-gray-900 text-sm uppercase tracking-wider">Tilfredse Kunder</h4>
                <p className="text-gray-500 text-xs mt-0.5">Stabilitet og mange tilbagevendende kunder</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sektion: Mere end 30 års erfaring */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-xs font-bold uppercase tracking-widest text-[#01529E] bg-[#01529E]/5 px-3 py-1.5 rounded-full">
                Erfaring & Ekspertise
              </span>
              <h2 className="text-3xl sm:text-4xl font-black text-gray-900 mt-4 leading-tight">
                Malerarbejde med kvalitet, erfaring og god service
              </h2>
              <div className="mt-6 space-y-4 text-gray-600 text-sm sm:text-base leading-relaxed">
                <p>
                  Malerfirma Villy Poulsen ApS har mere end 30 års erfaring i branchen. Gennem årene har vi hjulpet kunder i hele Østjylland med professionelle malerløsninger, hvor kompromisløs kvalitet og høj kundetilfredshed altid er i centrum.
                </p>
                <p>
                  Vi ved, hvor vigtigt det er, at det færdige resultat både fremstår smukt og beskytter i mange år frem. Derfor arbejder vi altid med stor præcision, sans for detaljen og materialer af højeste kvalitet, så du får et holdbart resultat, du kan glæde dig over hver dag.
                </p>
                <p>
                  Vores brede erfaring gør os i stand til at løse både små og store maleropgaver med samme høje engagement – uanset om det er for private boligejere, virksomheder, boligforeninger eller erhvervskunder.
                </p>
              </div>
            </div>

            <div className="relative">
              {/* Før/Efter Slider */}
              <div
                ref={containerRef}
                tabIndex={0}
                className="relative h-[450px] w-full rounded-2xl overflow-hidden shadow-2xl border border-gray-100 select-none cursor-ew-resize focus:outline-none focus:ring-2 focus:ring-[#01529E]/50 transition-shadow duration-200"
                onMouseDown={() => setIsDragging(true)}
                onTouchStart={() => setIsDragging(true)}
                onMouseMove={handleMouseMove}
                onTouchMove={handleTouchMove}
                onKeyDown={(e) => {
                  if (e.key === 'ArrowLeft') {
                    e.preventDefault()
                    setSliderPosition((prev) => Math.max(0, prev - 5))
                  } else if (e.key === 'ArrowRight') {
                    e.preventDefault()
                    setSliderPosition((prev) => Math.min(100, prev + 5))
                  }
                }}
              >
                {/* Efter Billede (Baggrund) */}
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets/bbcf0f14-2bcd-4cd4-98b4-2d62ce00d60f/image00002.jpeg"
                  alt="Efter malerarbejde"
                  className="absolute inset-0 h-full w-full object-cover"
                  draggable="false"
                />
                <div className="absolute right-4 bottom-4 bg-black/60 backdrop-blur-md text-white text-xs font-black px-3 py-1.5 rounded-full border border-white/20 uppercase tracking-wider z-10">
                  Efter
                </div>

                {/* Før Billede (Clipped overlay) */}
                <div
                  className="absolute inset-0 h-full overflow-hidden"
                  style={{ width: `${sliderPosition}%` }}
                >
                  <img
                    src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets/bbcf0f14-2bcd-4cd4-98b4-2d62ce00d60f/image00001.jpeg"
                    alt="Før malerarbejde"
                    className="absolute inset-0 h-full object-cover max-w-none"
                    style={{ width: containerWidth ? `${containerWidth}px` : '100%' }}
                    draggable="false"
                  />
                  <div className="absolute left-4 bottom-4 bg-black/60 backdrop-blur-md text-white text-xs font-black px-3 py-1.5 rounded-full border border-white/20 uppercase tracking-wider z-10">
                    Før
                  </div>
                </div>

                {/* Slider Handle Line */}
                <div
                  className="absolute top-0 bottom-0 w-0.5 bg-white/80 shadow-[0_0_10px_rgba(255,255,255,0.5)] cursor-ew-resize z-20"
                  style={{ left: `${sliderPosition}%` }}
                >
                  {/* Handle Button */}
                  <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-12 h-12 rounded-full bg-white/10 backdrop-blur-md border-2 border-white/80 shadow-[0_4px_30px_rgba(0,0,0,0.2)] flex items-center justify-center text-white transition-transform duration-200 hover:scale-110 active:scale-95 z-20">
                    <div className="flex items-center gap-1.5">
                      <ChevronLeft className="h-4 w-4 drop-shadow" />
                      <ChevronRight className="h-4 w-4 drop-shadow" />
                    </div>
                  </div>
                </div>
              </div>

              <div className="absolute -bottom-6 -left-6 bg-[#01529E] text-white p-6 rounded-2xl shadow-xl hidden sm:block z-30">
                <p className="text-4xl font-black">30+</p>
                <p className="text-xs font-bold uppercase tracking-wider mt-1">Års Erfaring i Branchen</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sektion: En tryg samarbejdspartner */}
      <section className="py-20 bg-gray-50 border-t border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <img
                src="https://assets.auxodev.dk/assets/bbcf0f14-2bcd-4cd4-98b4-2d62ce00d60f/9ffac026-6b24-4cce-9dc6-dce86789ca2e.jpeg"
                alt="En tryg samarbejdspartner"
                className="rounded-2xl shadow-2xl border border-gray-100 object-cover w-full h-[450px]"
              />
            </div>

            <div className="order-1 lg:order-2 space-y-6">
              <span className="text-xs font-bold uppercase tracking-widest text-[#01529E] bg-[#01529E]/5 px-3 py-1.5 rounded-full">
                Trygt Samarbejde
              </span>
              <h2 className="text-3xl sm:text-4xl font-black text-gray-900 mt-2">
                Ærligt håndværk og klare aftaler
              </h2>
              <div className="space-y-4 text-gray-600 text-sm sm:text-base leading-relaxed">
                <p>
                  Når du vælger Malerfirma Villy Poulsen ApS, får du en professionel samarbejdspartner, der sætter personlig service, kompromisløs kvalitet og en tæt, gennemskuelig dialog i højsædet. Vi bygger vores arbejde på ærligt håndværk, klare aftaler og skræddersyede løsninger, der matcher dine præcise ønsker og behov.
                </p>
                <p>
                  Vi har desuden et stærkt netværk af dygtige og pålidelige samarbejdspartnere på tværs af andre faggrupper. Vi hjælper derfor gerne med at formidle kontakten og fungere som den koordinerende tovholder på dit projekt. På den måde sikrer vi, at hele processen forløber fuldstændig glat, effektivt og til aftalt tid.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sektion: Vi hjælper blandt andet med */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-xs font-bold uppercase tracking-widest text-[#01529E] bg-[#01529E]/5 px-3 py-1.5 rounded-full">
              Vores Kompetencer
            </span>
            <h2 className="text-3xl sm:text-4xl font-black text-gray-900 mt-4">
              Vi hjælper blandt andet med
            </h2>
            <p className="text-gray-500 mt-2 text-sm sm:text-base">
              Vi tilbyder professionelle løsninger inden for alle typer malerarbejde i hele Østjylland.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-6 gap-8">
            {viHjaelperMed.map((item, idx) => {
              const IconComponent = item.icon
              // First 3 items take 2/6 (1/3) on desktop, last 2 items take 3/6 (1/2) on desktop.
              // On tablet (md), all items take 3/6 (1/2) width.
              const colSpanClass = idx < 3 
                ? "md:col-span-3 lg:col-span-2" 
                : "md:col-span-3 lg:col-span-3"

              return (
                <div key={idx} className={`${colSpanClass} bg-white rounded-2xl border border-gray-100 p-6 sm:p-8 shadow-sm hover:shadow-md transition-all flex flex-col h-full group`}>
                  <div className="p-3 rounded-xl bg-[#01529E]/5 text-[#01529E] shrink-0 w-12 h-12 flex items-center justify-center mb-6 group-hover:bg-[#01529E] group-hover:text-white transition-all">
                    <IconComponent className="h-6 w-6" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3">{item.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed mb-6 flex-grow">{item.desc}</p>
                  <Link
                    to={item.link}
                    className="text-[#01529E] font-bold text-xs hover:text-[#01529E]/80 flex items-center gap-1.5 mt-auto group/link"
                  >
                    Læs mere <ArrowRight className="h-3.5 w-3.5 transform group-hover/link:translate-x-1 transition-transform" />
                  </Link>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* Sektion: Vi løser opgaver for */}
      <section className="py-20 bg-gray-50 border-t border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="mb-10">
            <span className="inline-block text-xs font-bold uppercase tracking-widest text-[#01529E] bg-[#01529E]/5 px-3 py-1.5 rounded-full">
              Vores Kundegrupper
            </span>
            <h2 className="text-3xl sm:text-4xl font-black text-gray-900 mt-4 leading-tight">
              Vi løser opgaver for
            </h2>
            <p className="mt-4 text-gray-500 text-sm sm:text-base max-w-2xl mx-auto">
              Vi har kapaciteten og erfaringen til at håndtere projekter af enhver størrelse for en bred vifte af kunder.
            </p>
          </div>

          <div className="flex flex-wrap gap-4 items-center justify-center max-w-6xl mx-auto mt-10">
            {kundegrupper.map((kunde, idx) => {
              const IconComponent = kunde.icon
              return (
                <div
                  key={idx}
                  className="flex items-center gap-3 bg-white border border-gray-100 px-5 py-3.5 rounded-xl shadow-xs cursor-default select-none"
                >
                  <div className="w-10 h-10 rounded-lg bg-[#01529E]/5 text-[#01529E] flex items-center justify-center shrink-0">
                    <IconComponent className="h-5 w-5" />
                  </div>
                  <span className="text-sm sm:text-base text-gray-800 font-bold leading-none">
                    {kunde.name}
                  </span>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* Before / After Section */}
      <BeforeAfter />

      {/* FAQ Section */}
      <FAQSection />

      {/* CTA-sektion nederst på forsiden */}
      <section className="bg-[#20252C] text-white py-20 relative overflow-hidden">
        <div className="absolute right-0 bottom-0 translate-x-1/4 translate-y-1/4 w-96 h-96 bg-[#01529E]/10 rounded-full blur-3xl" />
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 text-center">
          <span className="text-xs font-bold uppercase tracking-widest text-[#ED1C26] bg-[#ED1C26]/10 px-3 py-1.5 rounded-full">
            Lad Os Komme I Gang
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black mt-4 mb-6">
            Skal vi hjælpe med dit næste malerprojekt?
          </h2>
          <p className="text-gray-300 text-base sm:text-lg max-w-3xl mx-auto mb-10 leading-relaxed">
            Vi ser frem til at hjælpe dig med dit næste malerprojekt. Kontakt Malerfirma Villy Poulsen ApS allerede i dag og få et uforpligtende tilbud på din opgave. Vi står klar til at rådgive dig og finde den bedste løsning til din bolig eller virksomhed.
          </p>

          <div className="bg-white/5 border border-white/10 rounded-2xl p-6 sm:p-8 max-w-2xl mx-auto mb-10">
            <h3 className="text-lg font-bold mb-4 text-[#ED1C26]">Direkte Kontakt</h3>
            <div className="flex flex-col sm:flex-row justify-center items-center gap-6 text-sm sm:text-base">
              <a href="tel:40637670" className="flex items-center gap-2 hover:text-[#ED1C26] transition-colors font-bold text-lg">
                <Phone className="h-5 w-5 text-[#ED1C26]" />
                Telefon: 40 63 76 70
              </a>
              <a href="mailto:mail@malerfirma-villy-poulsen.dk" className="flex items-center gap-2 hover:text-[#ED1C26] transition-colors font-bold text-lg">
                <Mail className="h-5 w-5 text-[#ED1C26]" />
                E-mail: mail@malerfirma-villy-poulsen.dk
              </a>
            </div>
          </div>

          <div>
            <Link
              to="/kontakt"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-[#01529E] text-white font-bold hover:bg-[#01529E]/90 transition-all shadow-lg cursor-pointer"
            >
              Kontakt os i dag
            </Link>
          </div>
        </div>
      </section>
    </div>
  )
}
